import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import Snackbars from '../../component/Snackbars'
import API from '../../component/Axios'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { LIST_FILTER_DATE_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { ADMIN_ACCESS_MODES } from '../../utils/sessionUtils'
import { formatSafeNumber as formatNumber } from '../../utils/numberDisplay'

const createEmptyCountryAnalytics = () => ({
    countries: [],
    monthly: [],
    rows: [],
    range: null,
})

// 기존 8개국은 쓰던 색을 유지한다. 운영자가 색으로 나라를 기억하고 있다.
const COUNTRY_CHART_COLORS = {
    MX: '#3182f6',
    BR: '#00a832',
    US: '#ff8a00',
    JP: '#f04452',
    CA: '#8b5cf6',
    GB: '#0891b2',
    ES: '#d97706',
    PT: '#db2777',
}

// 위에 없는 국가에 돌려쓰는 색. 한 차트에 4개국만 그리므로 4색이면 겹치지 않는다.
const COUNTRY_CHART_FALLBACK_COLORS = ['#3182f6', '#00a832', '#ff8a00', '#f04452']

const getCountryChartColor = (countryId, indexInGroup) => (
    COUNTRY_CHART_COLORS[countryId]
    || COUNTRY_CHART_FALLBACK_COLORS[indexInGroup % COUNTRY_CHART_FALLBACK_COLORS.length]
)

// 한 차트에 담을 국가 수. 더 넣으면 선이 겹쳐 읽기 어렵다.
const COUNTRY_CHART_GROUP_SIZE = 4

// 선택 국가를 4개씩 끊어 차트 묶음을 만든다. 예전에는 8개국을 두 그룹으로
// 하드코딩했는데, 운영자가 국가를 고르게 되면서 개수가 가변이 됐다.
const buildCountryChartGroups = (countries) => {
    const groups = []

    for (let index = 0; index < countries.length; index += COUNTRY_CHART_GROUP_SIZE) {
        const groupCountries = countries.slice(index, index + COUNTRY_CHART_GROUP_SIZE)

        groups.push({
            key: groupCountries.map(country => country.countryId).join('-'),
            title: groupCountries.map(country => country.name || country.countryId).join(' · '),
            countries: groupCountries,
        })
    }

    return groups
}

const formatMonthParam = (value) => {
    if (!value) {
        return ''
    }

    const date = value instanceof Date ? value : new Date(value)

    if (Number.isNaN(date.getTime())) {
        return ''
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function CountryAnalyticsChart({ chartData, countries, monthlyMap, title }) {
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                {countries.map((country, countryIndex) => (
                    <Box
                        key={country.countryId}
                        sx={{
                            minWidth: 132,
                            py: 0.75,
                            pr: 2,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">{country.name} MAU</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: getCountryChartColor(country.countryId, countryIndex) }}>
                            {formatNumber(monthlyMap[country.countryId]?.mau)}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ width: '100%', height: 420 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 12, right: 24, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={value => formatNumber(value)} tick={{ fontSize: 12 }} width={64} />
                        <Tooltip formatter={(value, name) => [formatNumber(value), name]} labelFormatter={label => `일자 ${label}`} />
                        <Legend />
                        {countries.map((country, countryIndex) => (
                            <Line
                                key={country.countryId}
                                type="monotone"
                                dataKey={country.countryId}
                                name={country.name}
                                stroke={getCountryChartColor(country.countryId, countryIndex)}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    )
}

export default function AppGeoAnalytics({ adminId, role, accessMode, countries, countryFilterSlot }) {
    const history = useHistory()
    const initialMonthRef = useRef(new Date())
    const requestSequenceRef = useRef(0)
    const [loading, setLoading] = useState(true)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('success')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [countryAnalytics, setCountryAnalytics] = useState(createEmptyCountryAnalytics)
    const [month, setMonth] = useState(initialMonthRef.current)
    // 국가를 바꿔 다시 조회할 때 운영자가 보던 월을 유지한다.
    const monthRef = useRef(initialMonthRef.current)

    useEffect(() => {
        monthRef.current = month
    }, [month])

    const monthlyMap = useMemo(() => {
        return (countryAnalytics.monthly || []).reduce((acc, item) => {
            acc[item.countryId] = item
            return acc
        }, {})
    }, [countryAnalytics.monthly])

    const chartData = useMemo(() => {
        return (countryAnalytics.rows || [])
            .slice()
            .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
            .map(row => {
                const item = {
                    date: `${row.dateKey.substring(4, 6)}-${row.dateKey.substring(6, 8)}`,
                }

                ;(countryAnalytics.countries || []).forEach(country => {
                    item[country.countryId] = Number(row.countries?.[country.countryId]?.dau) || 0
                })

                return item
            })
    }, [countryAnalytics.countries, countryAnalytics.rows])

    const chartGroups = useMemo(
        () => buildCountryChartGroups(countryAnalytics.countries || []),
        [countryAnalytics.countries],
    )

    const getData = useCallback(async (monthValue) => {
        const requestMonth = formatMonthParam(monthValue)

        if (!requestMonth) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('조회 월을 선택하세요.')
            return
        }

        const requestId = requestSequenceRef.current + 1
        requestSequenceRef.current = requestId
        setLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/dashboard/getCountryAnalyticsList', {
                adminId,
                role,
                month: requestMonth,
                countries,
            })

            if (requestId !== requestSequenceRef.current) {
                return
            }

            if (data.code === 200) {
                setCountryAnalytics(data.data || createEmptyCountryAnalytics())
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            if (requestId !== requestSequenceRef.current) {
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(err.response?.data?.message || err.message || '국가 통계 조회 중 오류가 발생했습니다.')
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false)
            }
        }
    }, [adminId, countries, history, role])

    // getData 가 countries 를 의존하므로 국가를 바꾸면 여기서 다시 조회된다.
    useEffect(() => {
        if (adminId && (role > 3 || accessMode === ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY)) {
            getData(monthRef.current)
        }
    }, [accessMode, adminId, getData, role])

    const handleSubmit = (e) => {
        e.preventDefault()
        getData(month)
    }

    const handleMonthChange = (nextMonth) => {
        setMonth(nextMonth)

        if (nextMonth) {
            getData(nextMonth)
        }
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    if (role <= 3 && accessMode !== ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY) {
        return null
    }

    return (
        <>
            <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Box>
                            <Typography variant="h6">국가별 앱 DAU/MAU</Typography>
                            <Typography variant="body2" color="text.secondary">
                                앱 GA4 activeUsers 기준이며, 월간 MAU는 선택 월 전체의 국가별 순사용자입니다.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                            {countryFilterSlot}
                            <ListFilterBar sx={{ width: 'auto', justifyContent: 'flex-end', mt: 0, mb: 0 }}>
                                <ListFilterForm onSubmit={handleSubmit}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            inputFormat="yyyy-MM"
                                            mask={"____-__"}
                                            openTo="month"
                                            views={['year','month']}
                                            minDate={new Date(2021, 0, 1)}
                                            maxDate={new Date()}
                                            value={month}
                                            onChange={handleMonthChange}
                                            renderInput={(params) => <TextField required {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} />}
                                        />
                                    </LocalizationProvider>
                                </ListFilterForm>
                            </ListFilterBar>
                        </Box>
                    </Box>
                    {loading ? (
                        <Spiner/>
                    ) : (countryAnalytics.countries || []).length === 0 ? (
                        <ListEmptyState
                            title="조회된 국가 통계 데이터가 없습니다."
                            description="조회 월을 변경한 뒤 다시 확인해보세요."
                        />
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {chartGroups.map(group => (
                                <CountryAnalyticsChart
                                    key={group.key}
                                    title={group.title}
                                    countries={group.countries}
                                    monthlyMap={monthlyMap}
                                    chartData={chartData}
                                />
                            ))}
                        </Box>
                    )}
                </Paper>
            </Grid>
            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
        </>
    )
}
